import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Col, Form, message, Modal, Row } from 'antd';
import styled from 'styled-components';
import { ICreateOrUpdateEmployeeBody, IEmployeeContent } from '../services/types';
import RadioGroup from './Input/RadioGroup';
import Input from './Input/Input';
import PhoneNumberInput from './Input/InputPhone';
import InputPassword from './Input/InputPassword';
import accountApis from '../../../services/accountApis';
import { AxiosResponse } from 'axios';

export enum RoleStatus {
  MANAGER = 'MANAGER',
  SALE = 'SALE',
  AGENT = 'AGENT',
  MARKETING = 'MARKETING',
}

interface Props {}

type FormValues = ICreateOrUpdateEmployeeBody;

export interface AddOrUpdateEmployeeModalRef {
  open: (data?: IEmployeeContent, onSuccess?: () => void) => void;
  close: () => void;
}

const AddOrUpdateEmployeeModal = forwardRef<AddOrUpdateEmployeeModalRef, Props>(({}, ref) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<IEmployeeContent>();
  const [loading, setLoading] = useState(false);
  const refSuccess = useRef<() => void>();

  const onOpen = (data?: IEmployeeContent, onSuccess?: () => void) => {
    setOpen(true);
    if (data) {
      setData(data);
      form.setFieldsValue(data);
    }
    refSuccess.current = onSuccess;
  };
  const onClose = () => {
    setOpen(false);
    setData(undefined);
    form.resetFields();
  };

  const onFinish = async (values: FormValues) => {
    try {
      setLoading(true);
      const body: ICreateOrUpdateEmployeeBody = {
        ...values,
        ...(data?.id && { id: data.id }),
      };
      const response: AxiosResponse<{ data: any }> = await accountApis.createOrUpdateEmployee(body);

      if (response.data.data) {
        message.success('Employee created successfully');
        onClose();
        refSuccess.current?.();
      } else {
        message.error('Failed to create employee');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onDone = () => {
    form.submit();
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  const options = [
    { value: RoleStatus.AGENT, label: 'Agent' },
    { value: RoleStatus.MANAGER, label: 'Manager' },
    { value: RoleStatus.SALE, label: 'Sale' },
    { value: RoleStatus.MARKETING, label: 'Marketing' },
  ];
  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      width={1048}
      centered
      destroyOnClose={false}
      closable={false}
    >
      <ContainerStyled>
        <TitleStyled>{data?.id ? 'Update' : 'Add'} Employee</TitleStyled>
        <Form form={form} onFinish={onFinish} layout='vertical'>
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <Form.Item
                required={false}
                name='roleName'
                label={
                  <span>
                    Role <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                initialValue={RoleStatus.AGENT}
                rules={[{ required: true, message: 'Please select a role' }]}
              >
                <RadioGroup options={options} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required={false}
                name='name'
                label={
                  <span>
                    Employee Name <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                rules={[{ required: true, message: 'Please enter employee name' }]}
              >
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required={false}
                name='phone'
                label={
                  <span>
                    Employee Phone <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                rules={[{ required: true, message: 'Please enter employee phone' }]}
              >
                <PhoneNumberInput placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required={false}
                name='email'
                label={
                  <span>
                    Employee Email <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: 'Please enter employee email',
                  },
                  {
                    type: 'email',
                    message: 'Please enter a valid email address',
                  },
                ]}
              >
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            {!data?.id && (
              <Col span={8}>
                <Form.Item
                  name='password'
                  label={
                    <span>
                      Password <span style={{ color: 'red' }}>*</span>
                    </span>
                  }
                  required={false}
                  rules={[{ required: true, message: 'Please enter password' }]}
                >
                  <InputPassword placeholder='Please enter...' />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item name='address' label='Address'>
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='city' label='City'>
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='state' label='State'>
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name='zip' label='Zip Code'>
                <Input placeholder='Please enter...' />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <ButtonsStyled>
          <button type='button' className='btn-cancel' onClick={onClose}>
            Close
          </button>
          <button type='button' className='btn-submit' onClick={onDone} disabled={loading}>
            Done
          </button>
        </ButtonsStyled>
      </ContainerStyled>
    </Modal>
  );
});
AddOrUpdateEmployeeModal.displayName = 'AddOrUpdateEmployeeModal';

export default AddOrUpdateEmployeeModal;

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  .ant-form-item {
    margin-bottom: 8px;
  }
`;

const TitleStyled = styled.div`
  align-self: stretch;
  color: #1d2129;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const ButtonsStyled = styled.div`
  display: flex;
  gap: 16px;

  button {
    cursor: pointer;
    display: flex;
    height: 48px;
    padding: 16px 24px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 4px;

    text-align: center;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    border: none;
    outline: none;
    transition: all 0.3s ease;
    &.btn-cancel {
      background: #e3e9ed;
      color: #566a7f;
    }
    &.btn-submit {
      color: #fff;
      background: #ff8890;
    }
    &:hover {
      opacity: 0.8;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;
